import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const SplashContainer = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  /* Use dynamic viewport height on modern mobile browsers to avoid address bar issues */
  height: 100dvh;
  background: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  cursor: ${props => (props.$videoEnded ? 'pointer' : 'default')};
  overscroll-behavior: contain;
  overflow: hidden;
  /* Responsive scale knob for the video */
  --splashScale: 1;

  /* iPhone Pro Max / large phones portrait */
  @media (max-width: 430px) and (orientation: portrait) {
    --splashScale: 0.86;
  }

  /* iPhone 12/13/14, Pixel 6-8 portrait */
  @media (max-width: 400px) and (orientation: portrait) {
    --splashScale: 0.8;
  }

  /* Very small devices */
  @media (max-width: 360px) and (orientation: portrait) {
    --splashScale: 0.74;
  }

  /* Short landscape viewports (mobile landscape) */
  @media (max-height: 520px) and (orientation: landscape) {
    --splashScale: 0.9;
  }
`;

const SplashVideo = styled.video`
  /* We'll size this element with explicit pixel dimensions for perfect contain */
  display: block;
  margin: 0 auto;
  object-fit: contain;
  object-position: center center;
  pointer-events: none; /* container handles taps */
  opacity: ${props => (props.$ready ? 1 : 0)};
  transition: opacity 200ms ease, transform 200ms ease;
  transform: scale(var(--splashScale, 1));
  transform-origin: center center;
`;

const SplashScreen = ({ onFinish }) => {
  const [videoEnded, setVideoEnded] = useState(false);
  const [ready, setReady] = useState(false);
  const videoRef = useRef(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });

  const computeDims = () => {
    const v = videoRef.current;
    if (!v) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const vratio = (v.videoWidth || 16) / (v.videoHeight || 9);
    const pratio = vw / vh;
    if (pratio > vratio) {
      // Limited by height
      const height = vh;
      const width = Math.min(vw, Math.round(height * vratio));
      setDims({ width, height });
    } else {
      // Limited by width
      const width = vw;
      const height = Math.min(vh, Math.round(width / vratio));
      setDims({ width, height });
    }
  };

  useEffect(() => {
    const onResize = () => computeDims();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const handleLoadedMetadata = () => {
    computeDims();
    setReady(true);
  };

  const handleEnded = () => setVideoEnded(true);

  const handleClick = () => {
    if (videoEnded && typeof onFinish === 'function') {
      onFinish();
    }
  };

  return (
    <SplashContainer $videoEnded={videoEnded} onClick={handleClick}>
      <SplashVideo
        ref={videoRef}
        src="/assets/SplashV1.mp4"
        autoPlay
        muted
        playsInline
        webkit-playsinline="true"
        preload="auto"
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        $ready={ready}
        style={{ width: dims.width, height: dims.height }}
      />
    </SplashContainer>
  );
};

export default SplashScreen;